import { IonHeader, IonPage, IonTitle, useIonViewWillEnter } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { PermissionExplainerModal } from "@/components/modal/app/permission-explainer";
import { CurrentDeviceCard } from "@/components/molecules/notifications/current-device-card";
import { DevicesListCard } from "@/components/molecules/notifications/devices-list-card";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { queryClient } from "@/lib/providers/query";
import { queryOptions_pushNotificationsPermissions } from "@/lib/providers/query/functions/@capacitor/push-notifications/permissions";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";
import { cleanupDeviceRegistration, resetPushPromptDismissal } from "@/lib/utils/notifications";
import { logger } from "@/logger";

const TOAST_KEY = "notification-preferences-update";

function NotificationTypeItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-surface-primary px-3 py-2.5">
      <div className="text-txt-secondary">{icon}</div>
      <span className="text-sm text-txt-primary">{label}</span>
    </div>
  );
}

export function SettingsNotificationsPage() {
  const { t } = useTranslation();
  const presentModal = useSelectorModalStore.use.present();

  const { data: permissionStatus, refetch: refetchPermissions } = useQuery(
    queryOptions_pushNotificationsPermissions(),
  );

  const { data: userDevices, refetch: refetchDevices } = useQuery({
    queryKey: ["notification", "devices"],
    queryFn: api.notification.getDevices,
    enabled: Capacitor.getPlatform() !== "web",
  });

  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
  const [showPermissionExplainer, setShowPermissionExplainer] = useState(false);

  // Get current device identifier on mount
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") {
      return;
    }

    import("@/lib/capacitor/device").then(({ Device }) => {
      Device.getDeviceIdentifier().then(setCurrentDeviceId);
    });
  }, []);

  // Determine if THIS device is registered (not just if user has any devices)
  useEffect(() => {
    if (!currentDeviceId || !userDevices?.data?.devices) {
      setIsNotificationEnabled(false);
      return;
    }

    const thisDevice = userDevices.data.devices.find(
      (device) => device.deviceIdentifier === currentDeviceId,
    );

    const hasPermission = permissionStatus === "granted";
    setIsNotificationEnabled(!!thisDevice && hasPermission);
  }, [userDevices, permissionStatus, currentDeviceId]);

  const requestPermissionsMutation = useMutation({
    mutationKey: ["@capacitor/push-notifications", "request-permissions"],
    mutationFn: async () => {
      if (Capacitor.getPlatform() === "web") {
        return "unavailable";
      }
      const permissions = await PushNotifications.requestPermissions();
      return permissions ?? "unknown";
    },
    onSettled: (data) => {
      queryClient.setQueryData(["@capacitor/push-notifications", "check-permissions"], data);
      refetchPermissions();
    },
  });

  const updateNotificationPreferencesMutation = useMutation({
    mutationKey: ["notification-preferences", "update"],
    mutationFn: async (input: { enable: boolean }) => {
      if (Capacitor.getPlatform() === "web") {
        toast.error(t("settings.notifications.status.web.error"), { id: TOAST_KEY });
        return;
      }

      if (input.enable) {
        let currentPermission = permissionStatus;

        // Show explainer modal if permission not determined yet (first time)
        if (currentPermission !== "granted" && currentPermission !== "denied") {
          setShowPermissionExplainer(true);
          throw new Error("Permission explainer shown");
        }

        if (currentPermission !== "granted") {
          const result = await requestPermissionsMutation.mutateAsync();
          currentPermission = typeof result === "string" ? result : result.receive;
        }
        if (currentPermission === "granted") {
          await PushNotifications.register();
          return { success: true };
        } else if (currentPermission === "denied") {
          presentModal("pushNotificationsDenied");
          throw new Error("Permission denied");
        } else {
          throw new Error("Permission not granted");
        }
      } else {
        // Use cleanup utility to unregister both locally and from backend
        await cleanupDeviceRegistration();
        return { success: true };
      }
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(TOAST_KEY);
    },
    onSuccess: () => {
      toast.success(t("actions:notification-preferences.success"), { id: TOAST_KEY });
      refetchDevices();
      refetchPermissions();
    },
    onError: (error) => {
      if (error.message !== "Permission explainer shown" && error.message !== "Permission denied") {
        toast.error(t("actions:notification-preferences.error"), { id: TOAST_KEY });
      }
      logger.error(error);
    },
  });

  const handleToggleNotifications = async () => {
    await updateNotificationPreferencesMutation.mutateAsync({ enable: !isNotificationEnabled });
  };

  useIonViewWillEnter(() => {
    refetchPermissions();
    refetchDevices();
    // Reset prompt dismissal when user manually visits settings
    // This allows them to see the prompt again where the app asks for permission
    resetPushPromptDismissal();
  });

  const isWeb = Capacitor.getPlatform() === "web";

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <Buttons slot="secondary">
            <BackButton defaultHref={Routes.Settings()} />
          </Buttons>
          <IonTitle>{t("routes:notifications.title")}</IonTitle>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome>
        <div className="flex flex-1 flex-col gap-4 py-4">
          {/* Web Platform Warning */}
          {isWeb && (
            <div className="mx-4 rounded-xl bg-warning-10 p-4">
              <div className="flex gap-3">
                <svg
                  className="size-6 shrink-0 text-warning-120"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-warning-120">
                    {t("settings.notifications.status.web.title")}
                  </h4>
                  <p className="mt-1 text-sm text-warning-120">
                    {t("settings.notifications.status.web.error")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Device Status Card */}
          {!isWeb && (
            <div className="mx-4">
              <CurrentDeviceCard
                isEnabled={isNotificationEnabled}
                permissionStatus={permissionStatus}
                onToggle={handleToggleNotifications}
                isLoading={updateNotificationPreferencesMutation.isPending}
              />
            </div>
          )}

          {/* Devices List Card */}
          {!isWeb && currentDeviceId && userDevices?.data?.devices && (
            <div className="mx-4">
              <DevicesListCard
                devices={userDevices.data.devices}
                currentDeviceId={currentDeviceId}
              />
            </div>
          )}

          {/* What you'll receive section */}
          {!isWeb && (
            <div className="mx-4">
              <h4 className="mb-3 text-sm font-semibold text-txt-primary">
                {t("settings.notifications.what-you-receive.title")}
              </h4>
              <div className="space-y-2">
                <NotificationTypeItem
                  icon={
                    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  }
                  label={t("settings.notifications.what-you-receive.types.schedule-changes")}
                />
                <NotificationTypeItem
                  icon={
                    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                      />
                    </svg>
                  }
                  label={t("settings.notifications.what-you-receive.types.team-updates")}
                />
                <NotificationTypeItem
                  icon={
                    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                  }
                  label={t("settings.notifications.what-you-receive.types.important-updates")}
                />
              </div>
            </div>
          )}
        </div>
      </Content>

      {/* Permission Explainer Modal */}
      <PermissionExplainerModal
        isOpen={showPermissionExplainer}
        onConfirm={async () => {
          setShowPermissionExplainer(false);
          // Request permission after user confirms
          const result = await requestPermissionsMutation.mutateAsync();
          const currentPermission = typeof result === "string" ? result : result.receive;

          if (currentPermission === "granted") {
            await PushNotifications.register();
            toast.success(t("settings.notifications.enabled-success"), { id: TOAST_KEY });
            refetchDevices();
            refetchPermissions();
          } else if (currentPermission === "denied") {
            presentModal("pushNotificationsDenied");
          }
        }}
        onDismiss={() => {
          setShowPermissionExplainer(false);
        }}
      />
    </IonPage>
  );
}
